import { z } from 'zod';
/**
 * Common primitive schemas reused across the data model.
 *
 * All timestamps are stored as Firestore Timestamps in production. In transit
 * (JSON over HTTP), they're serialized as ISO 8601 strings. The Zod schemas
 * accept the ISO string form; conversion to/from Firestore Timestamp happens
 * at the persistence boundary.
 */
export const SchoolId = z.string().min(1).max(64);
export const UserId = z.string().min(1).max(128);
export const QuizId = z.string().min(1).max(64);
export const QuestionId = z.string().min(1).max(64);
export const SessionId = z.string().min(1).max(64);
export const IsoTimestamp = z.string().datetime({ offset: true });
export const MediaRef = z.object({
    mediaId: z.string().min(1).max(128).optional(),
    kind: z.enum(['image', 'audio', 'video']),
    storagePath: z.string().min(1),
    mimeType: z.string().min(1),
    byteSize: z.number().int().nonnegative(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    durationSec: z.number().nonnegative().optional(),
    altText: z.string().max(500).optional(),
    caption: z.string().max(500).optional(),
});
export const MediaAssetStatus = z.enum(['pending', 'ready', 'deleted']);
export const MediaAttachmentReservation = z.object({
    quizId: QuizId,
    quizVersion: z.number().int().positive(),
    reservedAt: IsoTimestamp,
});
// A media asset belongs to exactly one scope: a quiz question/option being
// authored directly (quizId), or a reusable question-bank item (itemId) that
// has not yet been added to any quiz. When a bank item is added to a quiz,
// its media is copied into a new quizId-scoped asset — see
// api/src/lib/question-bank-media.ts — so quiz-time delivery and authorization
// never need to know about the bank.
export const MediaAsset = z.object({
    id: z.string().min(1).max(128), schoolId: SchoolId, ownerId: UserId,
    quizId: QuizId.optional(), itemId: z.string().min(1).max(128).optional(),
    questionId: QuestionId.optional(), optionId: z.string().min(1).max(8).optional(),
    kind: z.enum(['image', 'audio', 'video']),
    mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp', 'audio/mpeg', 'audio/wav', 'video/mp4', 'video/webm']),
    byteSize: z.number().int().positive(), width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(), durationSec: z.number().nonnegative().optional(),
    altText: z.string().max(500).optional(), caption: z.string().max(500).optional(),
    storagePath: z.string().min(1), checksum: z.string().min(1).optional(),
    attachmentReservation: MediaAttachmentReservation.optional(),
    status: MediaAssetStatus, sourceVersion: z.number().int().positive(),
    sourceRevision: z.string().min(1).max(128).optional(), createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp, uploadExpiresAt: IsoTimestamp,
}).refine((value) => Boolean(value.quizId) !== Boolean(value.itemId), {
    message: 'media must belong to exactly one of quizId or itemId',
});
export const Role = z.enum(['admin', 'teacher', 'proctor', 'student']);
//# sourceMappingURL=common.js.map